/**
 * Pestañas 2 y 3 del dashboard (doc 14/09 §2.2): New Clappers (Mandatory) y
 * Old Clappers (Optional). Cards, filtros y listado; exportación CSV solo para
 * People con acceso total (queda registrada).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import {
  adopcionVoluntaria, avancePromedioIniciados, countByStatus, cumplimientoNewClappers,
  MANDATORY_BUSINESS_DAYS, newClappers, oldClappers, type LearningDashboardRow,
} from '@/../../shared/people-learning';
import { StatCard } from '@/components/ui/stat-card';
import { ClapperFilters } from './ClapperFilters';
import { ClapperTable } from './ClapperTable';
import { EMPTY_FILTERS, TONE_KPI, antiguedad, applyFilters, categoryLabel, downloadCsv, filterOptions, fmtDate, fmtMs, pctTone, plazoLabel, rowPlazo, statusLabel, type RowFilters } from './learning-dash-utils';

export function ClappersTab({ rows, mode, scope, launchDate, today, onDetail }: {
  rows: LearningDashboardRow[];
  mode: 'new' | 'old';
  scope: 'all' | 'team';
  launchDate: string;
  today: string;
  onDetail: (email: string) => void;
}) {
  const { t } = useTranslation();
  const k = mode === 'new' ? 'people_learning.dash.new' : 'people_learning.dash.old';
  const [filters, setFilters] = useState<RowFilters>(EMPTY_FILTERS);
  const logExport = trpc.peopleLearning.logExport.useMutation();

  const subset = useMemo(() => (mode === 'new' ? newClappers(rows) : oldClappers(rows)), [rows, mode]);
  const options = useMemo(() => filterOptions(subset), [subset]);
  const filtered = useMemo(() => applyFilters(subset, filters), [subset, filters]);

  const exportCsv = () => {
    if (logExport.isPending) return;
    const header = ['Nombre', 'Email', 'Rol', 'Área', 'Equipo', 'País', 'Líder', 'Ingreso', mode === 'new' ? 'Plazo' : 'Antigüedad', 'Estado', 'Categoría', '% Avance', 'Tiempo', 'Puntos', 'Inicio', 'Fin', 'Videos vistos', 'Videos sin tiempo'];
    const lines = filtered.map((r) => [
      r.name, r.email, r.role, r.area, r.team, r.country, r.leaderName, r.startDate ?? '',
      mode === 'new' ? plazoLabel(t, rowPlazo(r, today)).text : antiguedad(t, r.startDate, today),
      statusLabel(t, r.status), categoryLabel(t, r.category), Math.round(r.progressPct * 100) + '%', fmtMs(r.elapsedMs), r.points,
      r.startedAt ?? '', r.finishedAt ?? '', r.engagement.videosDone, r.engagement.fastVideos,
    ]);
    downloadCsv(`learning-${mode === 'new' ? 'new-clappers' : 'old-clappers'}-${today}.csv`, header, lines);
    const active = Object.entries(filters).filter(([key, v]) => v && v !== '__all__' && key !== 'search').map(([key, v]) => `${key}=${v}`).join(',');
    logExport.mutate({ rowCount: filtered.length, filters: `${mode}${active ? ` · ${active}` : ''}` });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {mode === 'new'
          ? t(`${k}.note`, { date: fmtDate(launchDate), days: MANDATORY_BUSINESS_DAYS, defaultValue: 'Ingresos desde el {{date}} en adelante · plazo estándar: {{days}} días hábiles desde la fecha de ingreso. Completar el curso es obligatorio.' })
          : t(`${k}.note`, { date: fmtDate(launchDate), defaultValue: 'Ingresos anteriores al {{date}} · el curso es opcional para este grupo.' })}
      </p>

      {mode === 'new' ? <NewKpis rows={filtered} today={today} /> : <OldKpis rows={filtered} />}

      <ClapperFilters
        filters={filters}
        onChange={setFilters}
        options={options}
        show={{ area: true, leader: scope === 'all' }}
        onExport={scope === 'all' ? exportCsv : undefined}
        exporting={logExport.isPending}
      />

      <ClapperTable rows={filtered} mode={mode} today={today} onDetail={onDetail} />
    </div>
  );
}

function NewKpis({ rows, today }: { rows: LearningDashboardRow[]; today: string }) {
  const { t } = useTranslation();
  const k = 'people_learning.dash.new';
  const c = countByStatus(rows);
  const cumpl = cumplimientoNewClappers(rows, today);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <StatCard label={t(`${k}.kpi_total`, 'Total elegibles')} value={c.elegibles} sub={t(`${k}.kpi_total_hint`, { days: MANDATORY_BUSINESS_DAYS, defaultValue: 'Plazo estándar: {{days}} días hábiles' })} />
      <StatCard label={t(`${k}.kpi_cumplimiento`, '% de cumplimiento')} value={`${cumpl.pct}%`} fill={cumpl.total ? TONE_KPI[pctTone(cumpl.pct)] : 'neutral'} sub={t(`${k}.kpi_cumplimiento_hint`, { done: cumpl.done, late: cumpl.late, pending: cumpl.pending, defaultValue: '{{done}} a tiempo · {{late}} tarde · {{pending}} aún en plazo' })} />
      <StatCard label={t(`${k}.kpi_done`, 'Completados')} value={c.completados} fill="green" />
      <StatCard label={t(`${k}.kpi_progress`, 'En progreso')} value={c.enProgreso} fill="orange" />
      <StatCard label={t(`${k}.kpi_not_started`, 'Sin iniciar')} value={c.sinIniciar} fill="red" />
    </div>
  );
}

function OldKpis({ rows }: { rows: LearningDashboardRow[] }) {
  const { t } = useTranslation();
  const k = 'people_learning.dash.old';
  const c = countByStatus(rows);
  const adop = adopcionVoluntaria(rows);
  const avance = avancePromedioIniciados(rows);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard label={t(`${k}.kpi_total`, 'Total elegibles')} value={c.elegibles} />
      <StatCard label={t(`${k}.kpi_adopcion`, 'Adopción voluntaria')} value={`${adop.pct}%`} fill="green" sub={t(`${k}.kpi_adopcion_hint`, { done: adop.done, total: adop.total, defaultValue: '{{done}} de {{total}} completaron el 100%' })} />
      <StatCard label={t(`${k}.kpi_done`, 'Completado')} value={c.completados} />
      <StatCard label={t(`${k}.kpi_avance`, 'Avance promedio (iniciados)')} value={`${avance.pct}%`} sub={t(`${k}.kpi_avance_hint`, { count: avance.n, defaultValue: '{{count}} personas iniciaron el curso' })} />
    </div>
  );
}
